import React from 'react';

// Mesoamerican decorative background pattern
export const MesoamericanBackground = ({ className = '', opacity = 0.05 }) => (
  <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    <svg 
      className="absolute inset-0 w-full h-full" 
      style={{ opacity }}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="mesoPattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          {/* Central sun/flower motif */}
          <circle cx="40" cy="40" r="8" fill="none" stroke="#D97706" strokeWidth="1" />
          <circle cx="40" cy="40" r="4" fill="#D97706" />
          
          {/* Four directional points (cardinal directions - important in Mesoamerican cosmology) */}
          <path d="M40 20 L44 32 L40 28 L36 32 Z" fill="#D97706" />
          <path d="M60 40 L48 44 L52 40 L48 36 Z" fill="#D97706" />
          <path d="M40 60 L36 48 L40 52 L44 48 Z" fill="#D97706" />
          <path d="M20 40 L32 36 L28 40 L32 44 Z" fill="#D97706" />
          
          {/* Corner step patterns (grecas) */}
          <path d="M0 0 L12 0 L12 4 L4 4 L4 12 L0 12 Z" fill="none" stroke="#D97706" strokeWidth="0.5" />
          <path d="M80 0 L68 0 L68 4 L76 4 L76 12 L80 12 Z" fill="none" stroke="#D97706" strokeWidth="0.5" />
          <path d="M0 80 L12 80 L12 76 L4 76 L4 68 L0 68 Z" fill="none" stroke="#D97706" strokeWidth="0.5" />
          <path d="M80 80 L68 80 L68 76 L76 76 L76 68 L80 68 Z" fill="none" stroke="#D97706" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#mesoPattern)" />
    </svg>
  </div>
);

// Serpent-inspired wave border (Quetzalcoatl reference)
export const SerpentBorder = ({ className = '' }) => (
  <div className={`w-full overflow-hidden ${className}`}>
    <svg viewBox="0 0 400 24" className="w-full h-6" preserveAspectRatio="none">
      <defs>
        <pattern id="serpentPattern" x="0" y="0" width="100" height="24" patternUnits="userSpaceOnUse">
          <path
            d="M0 12 Q12.5 0, 25 12 T50 12 T75 12 T100 12"
            fill="none"
            stroke="#D97706"
            strokeWidth="2"
            opacity="0.6"
          />
          {/* Feather/scale details */}
          <circle cx="25" cy="12" r="2" fill="#F59E0B" opacity="0.4" />
          <circle cx="75" cy="12" r="2" fill="#F59E0B" opacity="0.4" />
        </pattern>
      </defs>
      <rect width="400" height="24" fill="url(#serpentPattern)" />
    </svg>
  </div>
);

// Pyramid step pattern divider
export const PyramidDivider = ({ className = '' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <svg viewBox="0 0 200 40" className="w-48 h-10">
      <defs>
        <linearGradient id="pyramidGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D97706" stopOpacity="0" />
          <stop offset="50%" stopColor="#D97706" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#D97706" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Horizontal lines */}
      <line x1="0" y1="20" x2="70" y2="20" stroke="url(#pyramidGrad)" strokeWidth="1" />
      <line x1="130" y1="20" x2="200" y2="20" stroke="url(#pyramidGrad)" strokeWidth="1" />
      
      {/* Center pyramid step motif */}
      <path
        d="M85 30 L90 30 L90 25 L95 25 L95 20 L100 20 L100 15 L105 15 L105 20 L110 20 L110 25 L115 25 L115 30 L120 30"
        fill="none"
        stroke="#D97706"
        strokeWidth="2"
      />
      
      {/* Top element */}
      <circle cx="100" cy="10" r="3" fill="#F59E0B" />
    </svg>
  </div>
);

// Calendar-inspired circular pattern
export const CalendarMotif = ({ size = 60, className = '' }) => (
  <div className={`${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <defs>
        <linearGradient id="calGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#92400E" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Outer ring with day signs */}
      <circle cx="30" cy="30" r="28" fill="none" stroke="#D97706" strokeWidth="1" opacity="0.4" />
      
      {/* 20 day signs represented as marks */}
      {Array.from({ length: 20 }).map((_, i) => {
        const angle = (i * 18 - 90) * (Math.PI / 180);
        const x1 = 30 + 24 * Math.cos(angle);
        const y1 = 30 + 24 * Math.sin(angle);
        const x2 = 30 + 28 * Math.cos(angle);
        const y2 = 30 + 28 * Math.sin(angle);
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#D97706"
            strokeWidth="1"
            opacity="0.5"
          />
        );
      })}
      
      {/* Inner ring */}
      <circle cx="30" cy="30" r="18" fill="none" stroke="#D97706" strokeWidth="1" opacity="0.3" />
      
      {/* Center face (tonatiuh - sun) */}
      <circle cx="30" cy="30" r="10" fill="url(#calGrad)" />
      <circle cx="30" cy="30" r="6" fill="#D97706" opacity="0.4" />
      
      {/* Cardinal directions */}
      {[0, 90, 180, 270].map((angle, i) => (
        <path
          key={i}
          d="M30 16 L32 22 L30 20 L28 22 Z"
          fill="#F59E0B"
          opacity="0.6"
          transform={`rotate(${angle} 30 30)`}
        />
      ))}
    </svg>
  </div>
);

// Feathered serpent line decoration
export const FeatheredLine = ({ className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="h-0.5 flex-1 bg-gradient-to-r from-transparent to-amber-600/40" />
    <svg viewBox="0 0 20 20" className="w-5 h-5 text-amber-600">
      <circle cx="10" cy="10" r="4" fill="currentColor" opacity="0.6" />
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <ellipse
          key={i}
          cx="10"
          cy="4"
          rx="2"
          ry="4"
          fill="currentColor"
          opacity="0.4"
          transform={`rotate(${angle} 10 10)`}
        />
      ))}
    </svg>
    <div className="h-0.5 flex-1 bg-gradient-to-l from-transparent to-amber-600/40" />
  </div>
);

export default {
  MesoamericanBackground,
  SerpentBorder,
  PyramidDivider,
  CalendarMotif,
  FeatheredLine
};
